// The two identities a face-match merge joins (ADR-0042), as the merge dialog
// names them — in the direction the merge is proposed, which the analyst may
// swap there (ADR-0052). Pure: the pair comes out of reads the caller already
// holds.
import type { MergeIdentitiesPair } from '@/components/faces/MergeIdentitiesDialog'
import type { ConfirmedMerge } from '@/features/identities/hooks/useMergedInto'
import type {
  FaceMatchingCandidate,
  FaceMatchingIdentityDetail,
} from '@/services/api/face-matching'
import type { DocumentFacePerson } from '@/services/api/photos'
import type { IdentityMergeTarget } from './hooks/useFaceMatching'

/**
 * The star's merge: the identity the candidate's face is confirmed to folds into
 * the identity the star is open for. Null when the candidate is gone or its face
 * belongs to nobody — then there is nothing to merge.
 */
export function starMergePair(
  candidate: FaceMatchingCandidate | undefined,
  survivor: Pick<FaceMatchingIdentityDetail, 'person_id' | 'display_name' | 'photo_url'>
): MergeIdentitiesPair | null {
  if (!candidate?.assigned_person_id) return null
  return {
    merged: {
      personId: candidate.assigned_person_id,
      displayName: candidate.assigned_display_name ?? null,
      photoUrl: candidate.assigned_photo_url ?? null,
    },
    survivor: {
      personId: survivor.person_id,
      displayName: survivor.display_name,
      photoUrl: survivor.photo_url,
    },
  }
}

/**
 * The overlay's merge: a confirmed face's identity folds into one of the face's
 * candidates. Null unless the face is still confirmed and still lists that
 * candidate.
 */
export function faceMergePair(
  faces: readonly DocumentFacePerson[] | undefined,
  target: IdentityMergeTarget | null
): MergeIdentitiesPair | null {
  if (!target) return null
  const face = faces?.find(item => item.face_id === target.faceId)
  const candidate = face?.candidates.find(item => item.person_id === target.personId)
  if (!face?.person_id || face.state !== 'confirmed' || !candidate) return null
  return {
    merged: {
      personId: face.person_id,
      displayName: face.display_name,
      photoUrl: face.photo_url,
    },
    survivor: {
      personId: candidate.person_id,
      displayName: candidate.display_name,
      photoUrl: candidate.photo_url,
    },
  }
}

/** The pair as the dialog confirmed it, by id: who the merge folds away and who stays. */
export function confirmedMerge({ merged, survivor }: MergeIdentitiesPair): ConfirmedMerge {
  return { mergedId: merged.personId, survivorId: survivor.personId }
}
